import React, { useState } from 'react';
import { usePage, Link, router } from '@inertiajs/react';
import { 
  Plus, Check, X, Clock, HelpCircle, FileText, ImageIcon, Video, File, MapPin, Search
} from 'lucide-react';
import AppShell, { AppShellProps } from '@/components/AppShell';

interface WhatsAppTemplate {
  id: number;
  template_name: string;
  language_code: string;
  template_category: string;
  status: string;
  template_id: string | null;
  created_at: string;
}

interface PageProps extends AppShellProps {
  whatsappAccount: {
    id: number;
    business_account_id: string;
    phone_number: string;
  };
  whatsappTemplates: WhatsAppTemplate[];
  flash?: { notice?: string; alert?: string };
}

export default function WhatsAppTemplatesIndex() {
  const props = usePage<PageProps>().props;
  const { whatsappAccount, whatsappTemplates, flash } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const handleDelete = (templateId: number) => {
    if (confirm('Are you sure you want to delete this template?')) {
      router.delete(`/whatsapp_templates/${templateId}`);
    }
  };

  const syncTemplates = () => {
    router.get('/whatsapp_templates/sync_all');
  };

  return (
    <AppShell {...props}>
      <div className="max-w-screen-xl mx-auto py-10 px-4 md:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">WhatsApp Templates</h1>
            <p className="text-gray-600 mt-1">Manage your Meta WhatsApp Business templates.</p>
          </div>
          <div className="flex space-x-3">
            <button 
              onClick={syncTemplates}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 border border-gray-300 rounded shadow-sm transition"
            >
              Sync from Meta
            </button>
            <Link
              href="/whatsapp_templates/new"
              className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded shadow-sm transition"
            >
              Create Template
            </Link>
          </div>
        </div>

        {flash?.alert && (
          <div className="bg-red-50 text-red-600 p-4 rounded mb-6 border border-red-200">
            {flash.alert}
          </div>
        )}
        {flash?.notice && (
          <div className="bg-green-50 text-green-600 p-4 rounded mb-6 border border-green-200">
            {flash.notice}
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100">
          <ul className="divide-y divide-gray-200">
            {whatsappTemplates?.length > 0 ? (
              whatsappTemplates.map((template) => (
                <li key={template.id}>
                  <div className="px-4 py-4 flex items-center sm:px-6">
                    <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <div className="flex text-sm">
                          <Link 
                            href={`/whatsapp_templates/${template.id}`} 
                            className="font-medium text-indigo-600 hover:text-indigo-500 truncate"
                          >
                            {template.template_name || `Template #${template.id}`}
                          </Link>
                          <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                             — {template.template_category}
                          </p>
                        </div>
                        <div className="mt-2 flex">
                          <div className="flex items-center text-sm text-gray-500">
                            Language: {template.language_code || 'en'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5">
                        <div className="flex space-x-4 items-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${template.status === 'approved' ? 'bg-green-100 text-green-800' : 
                              template.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'}`}>
                            {template.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                          
                          <div className="flex space-x-2 text-sm text-gray-600">
                            {['approved', 'rejected', 'paused'].includes(template.status || '') && (
                              <Link href={`/whatsapp_templates/${template.id}/edit`} className="hover:text-indigo-500">
                                Edit
                              </Link>
                            )}
                            <button onClick={() => handleDelete(template.id)} className="text-red-500 hover:text-red-700">
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <div className="px-4 py-8 text-center sm:px-6">
                <p className="text-gray-500 mb-2">No WhatsApp templates found.</p>
                <p className="text-sm text-gray-400">Click "Create Template" to get started or "Sync from Meta" to fetch existing templates.</p>
              </div>
            )}
          </ul>
        </div>
      </div>
    </AppShell>
  );
}
